import { Flex, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Abstract } from '@/module/Author/Abstract';
import { AbstractCard } from '@/module/Author/Abstract/components/Card/AbstractCard';
import { CriteriaAbstractSelectAddModal } from '@/module/Laboratory/criteria/components/CriteriaAbstractSelectAddModal';
import { getCenterReportCriteriaDataOptions } from '@/module/ResearchCenter';

import { NoContentPlaceholder, PendingSpinner } from '@/core/components';

import { Id } from '@feature/utils';

import { CriteriaHeaderButtons } from './components/components/CriteriaHeaderButtons';
import { CriteriaItemDeleteButton } from './components/components/CriteriaItemDeleteButton';
import { useSaveCriteriaData } from './components/hooks/useSaveCriteriaData';

export interface CenterReportAbstractSelectProps {
  labId: Id;
  criteriaCode: string;
}

export function CenterReportAbstractSelect({ labId, criteriaCode }: CenterReportAbstractSelectProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation();
  const {
    data: {
      completed,
      abstracts: { available, selected },
    },
  } = useSuspenseQuery(getCenterReportCriteriaDataOptions(labId, criteriaCode));
  const { onSubmit, isPending } = useSaveCriteriaData(labId, criteriaCode, close);

  const onAdd = (abstract: Abstract) => onSubmit({ toAdd: abstract });
  const onRemove = (abstract: Abstract) => onSubmit({ toRemove: abstract });

  return (
    <div>
      <CriteriaHeaderButtons
        completed={completed}
        criteriaCode={criteriaCode}
        labId={labId}
        onAdd={open}
      />
      {opened && (
        <CriteriaAbstractSelectAddModal
          available={available}
          selected={selected}
          close={close}
          onAdd={onAdd}
        />
      )}
      <>{isPending && <PendingSpinner />}</>
      {!isPending && (
        <>
          {selected.length > 0 && (
            <Stack>
              {selected.map((abstract, index) => (
                <Flex
                  align="center"
                  key={abstract.id}
                >
                  {!completed && (
                    <CriteriaItemDeleteButton
                      index={index}
                      onRemove={() => onRemove(abstract)}
                    />
                  )}
                  <AbstractCard
                    abstract={abstract}
                    onClick={() => window.open(`/author/abstract/${abstract.id}`)}
                  />
                </Flex>
              ))}
            </Stack>
          )}
          {selected.length === 0 && <NoContentPlaceholder title={t('centerReport.noCriteriaContent')} />}
        </>
      )}
    </div>
  );
}
